import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { PromptSemanticIndexingLeafFieldOption } from './prompt-schema/schemaFields';
import { getPromptSchemaFieldTypeCompactLabel } from './prompt-schema/schemaFields';
import './PromptSemanticIndexingFieldPicker.css';

interface PromptSemanticIndexingFieldPickerProps {
  fieldOptions: PromptSemanticIndexingLeafFieldOption[];
  disabledFieldPaths: string[];
  onChange?: (nextDisabledFieldPaths: string[]) => void;
  allowDynamicFields?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  emptyStateText?: string | null;
}

const PromptSemanticIndexingFieldPicker: React.FC<PromptSemanticIndexingFieldPickerProps> = ({
  fieldOptions,
  disabledFieldPaths,
  onChange,
  allowDynamicFields = false,
  disabled = false,
  readonly = false,
  emptyStateText = 'No declared leaf fields are available for semantic indexing.',
}) => {
  const normalizedDisabledFieldPaths = React.useMemo(
    () => Array.from(new Set(disabledFieldPaths.map((fieldPath) => fieldPath.trim()).filter(Boolean))),
    [disabledFieldPaths],
  );
  const disabledSet = React.useMemo(
    () => new Set(normalizedDisabledFieldPaths),
    [normalizedDisabledFieldPaths],
  );

  const enabledFieldOptions = fieldOptions.filter((field) => !disabledSet.has(field.fieldPath));
  const disabledFieldOptions = fieldOptions.filter((field) => disabledSet.has(field.fieldPath));

  const renderReadonlyFieldTag = (
    field: PromptSemanticIndexingLeafFieldOption,
    stateClassName: 'enabled' | 'disabled',
  ) => (
    <span key={field.fieldPath} className={`semantic-indexing-tag ${stateClassName}`}>
      <span className="semantic-indexing-tag-label">{field.displayLabel}</span>
      <span className="semantic-indexing-tag-type">
        {getPromptSchemaFieldTypeCompactLabel(field.type)}
      </span>
    </span>
  );

  const toggleFieldPath = (fieldPath: string) => {
    if (!onChange || readonly || disabled) {
      return;
    }

    const nextDisabledFieldPaths = disabledSet.has(fieldPath)
      ? normalizedDisabledFieldPaths.filter((value) => value !== fieldPath)
      : [...normalizedDisabledFieldPaths, fieldPath];
    onChange(nextDisabledFieldPaths);
  };

  return (
    <div className={`semantic-indexing-picker ${readonly ? 'readonly' : ''} ${disabled ? 'disabled' : ''}`}>
      {!readonly && (
        <>
          <div className="semantic-indexing-summary">
            <div className="semantic-indexing-count">
              <strong>{enabledFieldOptions.length}</strong>
              <span> / {fieldOptions.length} fields searchable</span>
            </div>
            {allowDynamicFields && (
              <div className="semantic-indexing-note">
                Dynamic additional fields stay searchable and are not configurable here.
              </div>
            )}
          </div>

          {fieldOptions.length === 0 ? (
            emptyStateText ? <div className="semantic-indexing-empty">{emptyStateText}</div> : null
          ) : (
            <div className="semantic-indexing-list">
              {fieldOptions.map((field) => {
                const isEnabled = !disabledSet.has(field.fieldPath);
                const typeLabel = getPromptSchemaFieldTypeCompactLabel(field.type);

                return (
                  <label
                    key={field.fieldPath}
                    className={`semantic-indexing-item ${isEnabled ? 'is-searchable' : 'is-excluded'}`}
                    title={field.displayLabel}
                  >
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      disabled={disabled}
                      onChange={() => toggleFieldPath(field.fieldPath)}
                    />
                    <span className="semantic-indexing-item-content">
                      <span className="semantic-indexing-item-header">
                        <span className="semantic-indexing-item-label">{field.displayLabel}</span>
                        <span className="semantic-indexing-item-type">{typeLabel}</span>
                        {field.warnsForSemanticSearch && (
                          <span className="semantic-indexing-warning" title="Short scalar fields often produce noisy semantic matches">
                            <AlertTriangle size={12} />
                            Scalar
                          </span>
                        )}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </>
      )}

      {readonly && fieldOptions.length > 0 && (
        <div className="semantic-indexing-readonly-summary">
          <div className="semantic-indexing-readonly-group">
            <span className="semantic-indexing-readonly-label">Searchable</span>
            <div className="semantic-indexing-readonly-tags">
              {enabledFieldOptions.length > 0 ? enabledFieldOptions.map((field) => (
                renderReadonlyFieldTag(field, 'enabled')
              )) : <span className="semantic-indexing-none">None</span>}
            </div>
          </div>
          <div className="semantic-indexing-readonly-group">
            <span className="semantic-indexing-readonly-label">Excluded</span>
            <div className="semantic-indexing-readonly-tags">
              {disabledFieldOptions.length > 0 ? disabledFieldOptions.map((field) => (
                renderReadonlyFieldTag(field, 'disabled')
              )) : <span className="semantic-indexing-none">None</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptSemanticIndexingFieldPicker;
